import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Divisions from "@/pages/Divisions";
import Journey from "@/pages/Journey";
import Contact from "@/pages/Contact";
import Careers from "@/pages/Careers";
import Login from "@/pages/Login";
import Portal from "@/pages/Portal";
import PortalInquiries from "@/pages/PortalInquiries";
import PortalHolidays from "@/pages/PortalHolidays";
import PortalDocuments from "@/pages/PortalDocuments";
import PortalRequests from "@/pages/PortalRequests";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";
import { AuthProvider, ProtectedRoute } from "@/context/AuthContext";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

const AppRouter = () => {
  const location = useLocation();
  // Process a fresh OAuth session_id BEFORE any route renders (race-condition safe)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/divisions" element={<Divisions />} />
      <Route path="/journey" element={<Journey />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <Portal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/inquiries"
        element={
          <ProtectedRoute>
            <PortalInquiries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/holidays"
        element={
          <ProtectedRoute>
            <PortalHolidays />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/documents"
        element={
          <ProtectedRoute>
            <PortalDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/requests"
        element={
          <ProtectedRoute>
            <PortalRequests />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollManager />
          <Navbar />
          <main>
            <AppRouter />
          </main>
          <Footer />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
